/**
 * Screen 2 — Invoice Register.
 *
 * Every filter lives in the URL. That is not a nicety: the detail view is a
 * real route, so returning from it must restore the filtered register rather
 * than resetting it, and the browser does that for free once the state is in
 * the address bar.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, Bookmark, BookmarkPlus, Download, LifeBuoy, Loader2, RotateCcw, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { STAGE_LABEL, int, money, num, useIptPrefs, useIptTheme } from './iptTheme';
import { Btn, Def, Empty, Input, Panel, Sample, StageChip } from './IptPrimitives';
import { IptRecoveryDialog } from './IptRecoveryDialog';

const MULTI = ['stage', 'wbs', 'currency', 'received_via'];
const FLAGS = [['blocked', 'Blocked'], ['overdue', 'Overdue'], ['disputed', 'Disputed']];
const COLS = [
  ['invoice_no', 'Invoice', 'left'],
  ['vendor_invoice_no', 'Vendor ref', 'left'],
  ['vendor_name', 'Vendor', 'left'],
  ['wbs_element', 'WBS', 'left'],
  ['receipt_date', 'Received', 'right'],
  ['due_date', 'Due', 'right'],
  ['current_stage', 'Stage', 'left'],
  ['__ageing', 'Ageing', 'right'],
  ['__beyond', 'Beyond terms', 'right'],
  ['net_payable', 'Net payable', 'right'],
];
const PAGE = 50;

export const IptRegister = ({ projectId, params, setParams, vimSource }) => {
  const T = useIptTheme();
  const prefs = useIptPrefs();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [views, setViews] = useState([]);
  const [q, setQ] = useState(params.get('search') || '');
  const [picked, setPicked] = useState([]);
  const [plan, setPlan] = useState(null);

  const sort = params.get('sort') || 'receipt_date';
  const direction = Number(params.get('direction') || -1);
  const skip = Number(params.get('skip') || 0);

  const query = useMemo(() => {
    const sp = new URLSearchParams();
    if (projectId) sp.set('project_id', projectId);
    ['search', 'vendor_id', 'ageing_bucket', 'origin', 'min_amount', 'max_amount',
      'receipt_from', 'receipt_to'].forEach((k) => {
      const v = params.get(k);
      if (v) sp.set(k, v);
    });
    MULTI.forEach((k) => (params.get(k) || '').split(',').filter(Boolean)
      .forEach((v) => sp.append(k, v)));
    FLAGS.forEach(([k]) => { if (params.get(k) === 'true') sp.set(k, 'true'); });
    sp.set('sort', sort);
    sp.set('direction', String(direction));
    sp.set('skip', String(skip));
    sp.set('limit', String(PAGE));
    sp.set('day_basis', prefs.dayBasis);
    sp.set('stop_clock', String(prefs.stopClock));
    return sp.toString();
  }, [projectId, params, sort, direction, skip, prefs.dayBasis, prefs.stopClock]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/ipt/invoices?${query}`).then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [query]);
  useEffect(load, [load]);
  useEffect(() => {
    api.get('/ipt/saved-views').then((r) => setViews(r.data.rows || r.data.views || []))
      .catch(() => setViews([]));
  }, []);

  const patch = (next) => {
    const sp = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') sp.delete(k);
      else sp.set(k, String(v));
    });
    if (!('skip' in next)) sp.delete('skip');
    setParams(sp, { replace: false });
  };

  const toggleMulti = (key, value) => {
    const cur = (params.get(key) || '').split(',').filter(Boolean);
    const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value];
    patch({ [key]: next.join(',') });
  };

  const active = useMemo(() => {
    const out = [];
    MULTI.forEach((k) => (params.get(k) || '').split(',').filter(Boolean)
      .forEach((v) => out.push([k, v, `${k}: ${STAGE_LABEL[v] || v}`])));
    ['search', 'vendor_id', 'ageing_bucket', 'origin', 'min_amount', 'max_amount',
      'receipt_from', 'receipt_to'].forEach((k) => {
      if (params.get(k)) out.push([k, null, `${k.replace(/_/g, ' ')}: ${params.get(k)}`]);
    });
    FLAGS.forEach(([k, label]) => {
      if (params.get(k) === 'true') out.push([k, null, label]);
    });
    return out;
  }, [params]);

  const clearAll = () => {
    const sp = new URLSearchParams();
    sp.set('tab', 'register');
    setQ('');
    setParams(sp);
  };

  const saveView = async () => {
    const name = window.prompt('Name this view');
    if (!name) return;
    try {
      await api.post('/ipt/saved-views', { name, filters: Object.fromEntries(params) });
      const r = await api.get('/ipt/saved-views');
      setViews(r.data.rows || r.data.views || []);
      toast.success(`Saved “${name}”`);
    } catch { toast.error('Could not save the view'); }
  };

  const exportCsv = () => {
    const sp = new URLSearchParams();
    if (projectId) sp.set('project_id', projectId);
    if (params.get('search')) sp.set('search', params.get('search'));
    (params.get('stage') || '').split(',').filter(Boolean).forEach((s) => sp.append('stage', s));
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/ipt/invoices/export?${sp.toString()}`;
    api.get(`/ipt/invoices/export?${sp.toString()}`, { responseType: 'blob' })
      .then((r) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(r.data);
        a.download = 'invoice_register.csv';
        a.click();
      })
      .catch(() => window.open(url, '_blank'));
  };

  const f = data?.facets || {};
  const rows = data?.rows || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4" data-testid="ipt-register">
      {/* ── facets ───────────────────────────────────────────────── */}
      <aside className="space-y-3">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5" style={{ color: T.muted }} />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') patch({ search: q }); }}
            placeholder="Invoice, vendor ref, PO…" data-testid="ipt-register-search"
            className="w-full text-[11.5px] rounded-md pl-8 pr-2 py-1.5 outline-none"
            style={{ background: T.surface, border: `1px solid ${T.border}`, color: T.ink }} />
        </div>

        <Facet T={T} title="Stage" items={f.stage} testId="facet-stage"
          selected={(params.get('stage') || '').split(',').filter(Boolean)}
          label={(v) => STAGE_LABEL[v] || v}
          onToggle={(v) => toggleMulti('stage', v)} />

        <Facet T={T} title="Ageing" items={f.ageingBucket} testId="facet-ageing"
          selected={params.get('ageing_bucket') ? [params.get('ageing_bucket')] : []}
          label={(v) => `${v} days`}
          onToggle={(v) => patch({ ageing_bucket: params.get('ageing_bucket') === v ? '' : v })} />

        <Panel T={T} title="Flags" pad={false} testId="facet-flags">
          <div className="p-2 space-y-1">
            {(f.flags || []).map((fl) => {
              const key = { blocked: 'blocked', overdue: 'overdue' }[fl.value];
              const on = key && params.get(key) === 'true';
              const clickable = Boolean(key);
              return (
                <button key={fl.value} type="button" disabled={!clickable}
                  data-testid={`ipt-flag-${fl.value}`}
                  onClick={() => key && patch({ [key]: on ? '' : 'true' })}
                  className="w-full flex items-center gap-2 px-1.5 py-1 rounded text-[11px] disabled:cursor-default"
                  style={on
                    ? { background: T.tint(T.primary, '1F'), color: T.primary }
                    : { color: T.body }}>
                  <span className="truncate text-left flex-1">{fl.label}</span>
                  <span className="tabular-nums text-[10px]" style={{ color: T.muted }}>
                    {int(fl.count)}
                  </span>
                </button>
              );
            })}
          </div>
        </Panel>

        <Facet T={T} title="Vendor" items={(f.vendor || []).slice(0, 10)} testId="facet-vendor"
          selected={params.get('search') ? [params.get('search')] : []}
          onToggle={(v) => { setQ(v); patch({ search: params.get('search') === v ? '' : v }); }} />

        <Facet T={T} title="Received via" items={f.receivedVia} testId="facet-received-via"
          selected={(params.get('received_via') || '').split(',').filter(Boolean)}
          onToggle={(v) => toggleMulti('received_via', v)}
          mark={(v) => (v === 'SAP VIM' && vimSource?.simulated ? 'simulated' : null)} />

        <Facet T={T} title="WBS" items={(f.wbs || []).slice(0, 10)} testId="facet-wbs"
          selected={(params.get('wbs') || '').split(',').filter(Boolean)}
          onToggle={(v) => toggleMulti('wbs', v)} />

        <Panel T={T} title="Amount" testId="facet-amount">
          <div className="flex items-center gap-2">
            <Input T={T} type="number" placeholder="min" data-testid="ipt-min-amount"
              defaultValue={params.get('min_amount') || ''}
              onBlur={(e) => patch({ min_amount: e.target.value })} />
            <Input T={T} type="number" placeholder="max" data-testid="ipt-max-amount"
              defaultValue={params.get('max_amount') || ''}
              onBlur={(e) => patch({ max_amount: e.target.value })} />
          </div>
        </Panel>

        {views.length > 0 && (
          <Panel T={T} title="Saved views" pad={false} testId="ipt-saved-views">
            <div className="p-2 space-y-1">
              {views.map((v) => (
                <button key={v.view_id || v.name} type="button"
                  data-testid={`ipt-saved-view-${v.name}`}
                  onClick={() => setParams(new URLSearchParams({ tab: 'register', ...(v.filters || {}) }))}
                  className="w-full flex items-center gap-1.5 px-1.5 py-1 rounded text-[11px] text-left hover:opacity-80"
                  style={{ color: T.body }}>
                  <Bookmark className="w-3 h-3 shrink-0" style={{ color: T.primary }} />
                  <span className="truncate">{v.name}</span>
                </button>
              ))}
            </div>
          </Panel>
        )}
      </aside>

      {/* ── table ────────────────────────────────────────────────── */}
      <div className="min-w-0 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[12px]" data-testid="ipt-register-count">
            <span className="font-bold tabular-nums" style={{ color: T.ink }}>
              {int(data?.total)}
            </span>
            <span style={{ color: T.body }}> invoices · </span>
            <span className="font-bold tabular-nums" style={{ color: T.ink }}>
              {money(data?.value, rows[0]?.currency || 'INR')}
            </span>
            <span style={{ color: T.body }}> net payable</span>
          </div>
          <Def T={T} testId="ipt-register-def" title="Register totals"
            body="Count and net payable over the filtered set, before pagination. Net payable is gross less every applied deduction."
            basis={data?.basis}
            sample={`Showing ${int(rows.length)} of ${int(data?.total)} rows.`} />
          <div className="ml-auto flex items-center gap-2">
            <Btn T={T} variant="ghost" onClick={saveView} testId="ipt-save-view-btn">
              <BookmarkPlus className="w-3.5 h-3.5" /> Save view
            </Btn>
            <Btn T={T} variant="ghost" onClick={exportCsv} testId="ipt-export-btn">
              <Download className="w-3.5 h-3.5" /> CSV
            </Btn>
          </div>
        </div>

        {picked.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap rounded-md px-3 py-2"
            data-testid="ipt-selection-bar"
            style={{ background: T.tint(T.primary, '12'),
                     border: `1px solid ${T.tint(T.primary, '33')}` }}>
            <span className="text-[11px] font-semibold" style={{ color: T.ink }}
              data-testid="ipt-selection-count">
              {int(picked.length)} selected
            </span>
            <span className="text-[10px]" style={{ color: T.muted }}>
              A recovery plan names the owner, the blocker and the next action for each one,
              and totals what is recoverable.
            </span>
            <span className="ml-auto flex items-center gap-2">
              <Btn T={T} testId="ipt-recovery-btn" onClick={() => setPlan({ ids: picked })}>
                <LifeBuoy className="w-3.5 h-3.5" /> Recovery plan
              </Btn>
              <Btn T={T} variant="ghost" testId="ipt-selection-clear"
                onClick={() => setPicked([])}>clear</Btn>
            </span>
          </div>
        )}

        {active.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="ipt-active-filters">
            {active.map(([k, v, label]) => (
              <button key={`${k}-${v || ''}`} type="button"
                onClick={() => {
                  if (v && MULTI.includes(k)) toggleMulti(k, v);
                  else { if (k === 'search') setQ(''); patch({ [k]: '' }); }
                }}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                style={{ background: T.tint(T.primary, '1A'), color: T.primary,
                         border: `1px solid ${T.tint(T.primary, '33')}` }}>
                {label}<X className="w-2.5 h-2.5" />
              </button>
            ))}
            <button type="button" onClick={clearAll} data-testid="ipt-clear-filters"
              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5"
              style={{ color: T.muted }}>
              <RotateCcw className="w-2.5 h-2.5" /> clear all
            </button>
          </div>
        )}

        <div className="rounded-lg overflow-hidden"
          style={{ background: T.surface, border: `1px solid ${T.border}` }}>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="ipt-register-table">
              <thead>
                <tr style={{ background: T.surfaceAlt, color: T.muted }}>
                  <th className="py-2 px-2 w-8">
                    <input type="checkbox" data-testid="ipt-select-all"
                      aria-label="Select every invoice on this page"
                      checked={rows.length > 0 && picked.length === rows.length}
                      onChange={(e) => setPicked(e.target.checked
                        ? rows.map((r) => r.invoice_id) : [])} />
                  </th>
                  {COLS.map(([key, label, align]) => {
                    const sortable = !key.startsWith('__');
                    return (
                      <th key={key} data-testid={`ipt-col-${key}`}
                        onClick={() => sortable && patch({
                          sort: key, direction: sort === key ? -direction : -1,
                        })}
                        className={`py-2 px-2 font-semibold whitespace-nowrap ${align === 'right' ? 'text-right' : 'text-left'} ${sortable ? 'cursor-pointer' : ''}`}>
                        <span className="inline-flex items-center gap-1">
                          {label}
                          {sort === key && (direction === 1
                            ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={COLS.length + 1} className="py-10 text-center">
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" style={{ color: T.primary }} />
                  </td></tr>
                )}
                {!loading && rows.map((r) => {
                  const m = r.metrics || {};
                  return (
                    <tr key={r.invoice_id} data-testid={`ipt-row-${r.invoice_no}`}
                      onClick={() => navigate(
                        `/project-controls/cost/invoices/${r.invoice_id}?tab=lifecycle`)}
                      className="cursor-pointer transition-colors"
                      style={{ borderTop: `1px solid ${T.border}` }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = T.surfaceAlt; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                      <td className="py-1.5 px-2" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" data-testid={`ipt-select-${r.invoice_no}`}
                          aria-label={`Select ${r.invoice_no}`}
                          checked={picked.includes(r.invoice_id)}
                          onChange={(e) => setPicked((p) => (e.target.checked
                            ? [...p, r.invoice_id] : p.filter((x) => x !== r.invoice_id)))} />
                      </td>
                      <td className="py-1.5 px-2 whitespace-nowrap">
                        <span className="font-semibold" style={{ color: T.primary }}>
                          {r.invoice_no}
                        </span>
                        <div className="flex gap-1 mt-0.5">
                          {r.is_blocked && <Tag T={T} tone={T.red}>blocked</Tag>}
                          {r.imported_without_history && <Tag T={T} tone={T.amber}>imported</Tag>}
                          {r.received_via === 'SAP VIM' && vimSource?.simulated
                            && <Tag T={T} tone={T.accent}>VIM sim</Tag>}
                        </div>
                      </td>
                      <td className="py-1.5 px-2 whitespace-nowrap" style={{ color: T.body }}>
                        {r.vendor_invoice_no || '—'}
                      </td>
                      <td className="py-1.5 px-2 truncate max-w-[170px]" style={{ color: T.ink }}>
                        {r.vendor_name}
                      </td>
                      <td className="py-1.5 px-2 font-mono text-[10px]" style={{ color: T.body }}>
                        {r.wbs_element || '—'}
                      </td>
                      <td className="py-1.5 px-2 text-right tabular-nums whitespace-nowrap"
                        style={{ color: T.body }}>{(r.receipt_date || '').slice(0, 10)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums whitespace-nowrap"
                        style={{ color: T.body }}>{(r.due_date || '').slice(0, 10)}</td>
                      <td className="py-1.5 px-2"><StageChip stage={r.current_stage} T={T} /></td>
                      <td className="py-1.5 px-2 text-right tabular-nums" style={{ color: T.ink }}>
                        {m.ageing_days === null ? '—' : num(m.ageing_days, 0)}
                      </td>
                      <td className="py-1.5 px-2 text-right tabular-nums font-semibold"
                        style={{ color: m.days_beyond_terms > 0 ? T.red : T.muted }}>
                        {m.days_beyond_terms > 0 ? num(m.days_beyond_terms, 0) : '—'}
                      </td>
                      <td className="py-1.5 px-2 text-right tabular-nums font-semibold"
                        style={{ color: T.ink }}>{money(r.net_payable, r.currency)}</td>
                    </tr>
                  );
                })}
                {!loading && !rows.length && (
                  <tr><td colSpan={COLS.length + 1}>
                    <Empty T={T} testId="ipt-register-empty">
                      No invoices match these filters.
                    </Empty>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center gap-2 text-[11px]" style={{ color: T.body }}>
          <Btn T={T} variant="ghost" disabled={skip <= 0} testId="ipt-page-prev"
            onClick={() => patch({ skip: Math.max(0, skip - PAGE) })}>Previous</Btn>
          <span className="tabular-nums">
            {int(skip + 1)}–{int(Math.min(skip + PAGE, data?.total || 0))} of {int(data?.total)}
          </span>
          <Btn T={T} variant="ghost" disabled={skip + PAGE >= (data?.total || 0)}
            testId="ipt-page-next" onClick={() => patch({ skip: skip + PAGE })}>Next</Btn>
        </div>

        <Sample T={T} testId="ipt-register-basis"
          text={`Ageing and beyond-terms are computed on ${data?.basis} from the transition `
            + 'table at read time — nothing time-derived is stored on the invoice. '
            + 'Rows marked “imported” carry a single synthetic transition and are excluded '
            + 'from every dwell-time figure elsewhere in the module.'} />
      </div>
      {plan && (
        <IptRecoveryDialog invoiceIds={plan.ids} projectId={projectId} T={T}
          onClose={() => setPlan(null)} />
      )}
    </div>
  );
};

const Tag = ({ children, T, tone }) => (
  <span className="px-1 py-px rounded text-[8.5px] font-bold uppercase tracking-wide"
    style={{ background: T.tint(tone, '1A'), color: tone }}>{children}</span>
);

const Facet = ({ title, items, selected, onToggle, label, mark, T, testId }) => (
  !items || !items.length ? null : (
    <Panel T={T} title={title} pad={false} testId={testId}>
      <div className="p-2 space-y-1 max-h-[210px] overflow-y-auto">
        {items.map((it) => {
          const on = selected.includes(it.value);
          const m = mark ? mark(it.value) : null;
          return (
            <button key={it.value} type="button" data-testid={`${testId}-${it.value}`}
              onClick={() => onToggle(it.value)}
              className="w-full flex items-center gap-2 px-1.5 py-1 rounded text-[11px]"
              style={on
                ? { background: T.tint(T.primary, '1F'), color: T.primary }
                : { color: T.body }}>
              <span className="truncate text-left flex-1">
                {label ? label(it.value) : it.value}
                {m && (
                  <span className="ml-1 px-1 rounded text-[8px] font-bold uppercase"
                    style={{ background: T.tint(T.accent, '1A'), color: T.accent }}>{m}</span>
                )}
              </span>
              <span className="tabular-nums text-[10px]" style={{ color: T.muted }}>
                {int(it.count)}
              </span>
            </button>
          );
        })}
      </div>
    </Panel>
  )
);

export default IptRegister;
